import React from 'react';
import { Heading, Text } from '../../../../components/Typography';
import { DocumentSection } from '../../../../store/summaryTest/types';
import styles from './DocumentSections.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import {
  setSelectedSectionsForDocument,
  setUseEmbeddingsForDocument,
} from '../../../../store/summaryTest/slice';
import Button from '../../../../components/UI/Button/Button';
import Checkbox from '../../../../components/UI/Checkbox';

interface DocumentSectionsProps {
  documentId: string;
  documentName: string;
  sections: DocumentSection[];
  maxCharsPerSection?: number | null;
}

const DocumentSections: React.FC<DocumentSectionsProps> = ({
  documentId,
  documentName,
  sections,
  maxCharsPerSection,
}) => {
  const dispatch = useDispatch();
  const selection = useSelector(
    (state: RootState) =>
      state.summaryTest.selectedDocuments[documentId] || {
        selectedSectionIdx: [],
        useEmbeddings: false,
      },
  );
  const { selectedSectionIdx, useEmbeddings } = selection;
  const hasEmbeddings = useSelector((state: RootState) => {
    const doc = state.summaryTest.documents.find((d) => d.id === documentId);
    return doc?.hasEmbeddings ?? false;
  });

  // Handlers
  const handleSectionToggle = (idx: number) => {
    const exists = selectedSectionIdx.includes(idx);
    const newSelected = exists
      ? selectedSectionIdx.filter((i) => i !== idx)
      : [...selectedSectionIdx, idx];
    dispatch(setSelectedSectionsForDocument({ documentId, sectionIdx: newSelected }));
  };
  const handleSelectAllSections = () => {
    dispatch(
      setSelectedSectionsForDocument({
        documentId,
        sectionIdx: sections.map((_, idx) => idx),
      }),
    );
  };
  const handleDeselectAllSections = () => {
    dispatch(setSelectedSectionsForDocument({ documentId, sectionIdx: [] }));
  };
  const handleUseEmbeddingsToggle = (checked: boolean) => {
    dispatch(setUseEmbeddingsForDocument({ documentId, useEmbeddings: checked }));
  };

  const getSectionLabel = (section: DocumentSection) => {
    const isTruncated =
      maxCharsPerSection !== undefined &&
      maxCharsPerSection !== null &&
      section.characterCount > maxCharsPerSection;
    return (
      <div className={styles.sectionContent}>
        <Text className={styles.sectionTitle}>{section.header || 'Untitled Section'}</Text>
        <Text size="small" color="neutral-500" className={styles.characterCount}>
          {section.characterCount.toLocaleString()} characters
        </Text>
        {isTruncated && (
          <Text size="small" color="warning" className={styles.truncatedInfo}>
            Truncated to {maxCharsPerSection.toLocaleString()} characters
          </Text>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.documentHeader}>
        <Heading level={4}>{documentName}</Heading>
        <Checkbox
          label="Use Embeddings"
          checked={useEmbeddings}
          onChange={handleUseEmbeddingsToggle}
          disabled={!hasEmbeddings}
          className={styles.useEmbeddingsLabel}
        />
        {!hasEmbeddings && <span className={styles.noEmbeddingsMsg}>No embeddings available</span>}
        <Button
          variant="secondary"
          size="small"
          onClick={handleSelectAllSections}
          className={styles.selectAllButton}
        >
          Select All
        </Button>
        <Button
          variant="secondary"
          size="small"
          onClick={handleDeselectAllSections}
          className={styles.deselectAllButton}
        >
          Deselect All
        </Button>
      </div>

      <div className={styles.sectionsContainer}>
        {sections.map((section, index) => {
          return (
            <Checkbox
              key={section.idx}
              label={getSectionLabel(section)}
              checked={selectedSectionIdx.includes(index)}
              onChange={() => handleSectionToggle(index)}
            />
          );
        })}
      </div>
    </div>
  );
};

export default DocumentSections;
